import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,  // H3 FIX: Rely on httpOnly cookies for auth
  timeout: 30000,
});

// H3 FIX: Removed localStorage token injection - using httpOnly cookies only
api.interceptors.request.use(config => config);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve(token));
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle 401 — attempt token refresh and retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      const detail = error.response?.data?.detail;

      // Session replaced by another login — immediate logout, no retry
      if (detail === 'SESSION_REPLACED') {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath.indexOf('/supplier-portal') === -1) {
          // Guard against multiple parallel 401s stacking toasts/timeouts
          if (!window.__sessionReplacedHandled) {
            window.__sessionReplacedHandled = true;
            toast.dismiss();
            toast.error('We have detected another login. This session has been terminated.', { id: 'session-replaced', duration: 2000 });
            setTimeout(() => { window.location.href = '/login'; }, 2000);
          }
        }
        return Promise.reject(error);
      }

      const path = window.location.pathname;
      // Don't redirect on public pages or supplier portal (has its own auth)
      if (path === '/login' || path === '/eyvo-admin' || path.indexOf('/reset-password') !== -1 || 
          path.indexOf('/approve-external') !== -1 || path.indexOf('/supplier-portal') !== -1 ||
          path.indexOf('/public_RFX') !== -1 || path.indexOf('/onboard') !== -1 ||
          path.indexOf('/design-lab') !== -1) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // H3 FIX: Refresh endpoint uses httpOnly cookies automatically via withCredentials
        await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return api(originalRequest);
      } catch {
        // Refresh failed — redirect to login (except for public pages)
        processQueue(error);
        // Only redirect if not already on login page or public pages
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && 
            currentPath.indexOf('/supplier-portal') === -1 &&
            currentPath.indexOf('/public_RFX') === -1 &&
            currentPath.indexOf('/onboard') === -1 &&
            currentPath.indexOf('/design-lab') === -1) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 409) {
      toast.error(error.response.data?.detail || 'Record modified by another user. Please refresh.', { duration: 8000 });
    }

    // Handle Pydantic 422 validation errors — detail is an array of objects,
    // not a string. Extract human-readable messages to prevent React render crash.
    if (error.response?.status === 422) {
      const detail = error.response.data?.detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map(d => {
          const field = (d.loc || []).slice(1).join('.');
          return field ? `${field}: ${d.msg}` : d.msg;
        }).join('; ');
        toast.error(msgs || 'Validation error', { duration: 6000 });
      }
    }

    return Promise.reject(error);
  }
);

export default api;

/**
 * Safely extract an error message string from an API error response.
 * Handles Pydantic 422 detail arrays, string details, and generic errors.
 * Use this instead of `e.response?.data?.detail` to prevent React render crashes.
 */
export function getApiErrorMessage(error, fallback = 'An error occurred') {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (typeof d === 'string') return d;
      const field = (d.loc || []).slice(1).join('.');
      return field ? `${field}: ${d.msg}` : (d.msg || JSON.stringify(d));
    }).join('; ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return fallback;
}
